/**
 * Zod-Schemas for the Revenue Alert Engine (Phase 4).
 *
 * Mirror the row shape of `public.revenue_alerts` (migration
 * `20260520000005_revenue_alerts.sql`). Used by the repository
 * (`apps/nutriflow/src/data/revenueAlerts.ts`) to validate inbound
 * rows from Supabase and by the smart-card / banner components to
 * type their props.
 *
 * Pure module — no React, no Supabase.
 */

import { z } from 'zod';

/**
 * Severity tier — drives UI surfacing. Mirrors the DB CHECK constraint.
 */
export const zRevenueAlertSeverity = z.enum(['info', 'recommended', 'critical']);
export type RevenueAlertSeverity = z.infer<typeof zRevenueAlertSeverity>;

/** Lifecycle status mirrored from the DB CHECK constraint. */
export const zRevenueAlertStatus = z.enum(['pending', 'acted', 'dismissed', 'expired']);
export type RevenueAlertStatus = z.infer<typeof zRevenueAlertStatus>;

/** Which UI action did the clinician take? */
export const zRevenueAlertAction = z.enum([
  'diagnosis_added',
  'entlassbrief_started',
  'consult_requested',
  'manual_dismiss',
]);
export type RevenueAlertAction = z.infer<typeof zRevenueAlertAction>;

/** ICD-10-GM malnutrition codes the engine may suggest. */
export const zRevenueSuggestedIcd = z.enum(['E43', 'E44', 'E46']);
export type RevenueSuggestedIcd = z.infer<typeof zRevenueSuggestedIcd>;

/**
 * Full row shape. Datetime fields are accepted as ISO strings (the
 * shape Supabase returns).
 */
export const zRevenueAlert = z.object({
  id: z.string().uuid(),
  org_id: z.string().uuid(),
  patient_id: z.string().uuid(),
  encounter_id: z.string().uuid().nullable(),

  rule_id: z.string().min(1).max(64),
  severity: zRevenueAlertSeverity,
  suggested_icd: zRevenueSuggestedIcd.nullable(),

  rationale: z.string().min(1).max(2000),
  citations: z.array(z.string()),
  estimated_roi_eur: z.number().nullable(),

  status: zRevenueAlertStatus,
  acted_by: z.string().uuid().nullable(),
  // Supabase emits ISO with timezone offset for timestamptz.
  acted_at: z.string().datetime({ offset: true }).nullable(),
  acted_action: zRevenueAlertAction.nullable(),

  created_at: z.string().datetime({ offset: true }),
  expires_at: z.string().datetime({ offset: true }).nullable(),
});

export type RevenueAlert = z.infer<typeof zRevenueAlert>;

/**
 * Payload accepted by the "Mark as acted" mutation. Validated client-
 * side before issuing the Supabase update so we fail loudly on typos.
 */
export const zRevenueAlertActionPayload = z.object({
  alertId: z.string().uuid(),
  action: zRevenueAlertAction,
  actorId: z.string().uuid(),
  /**
   * Required when dismissing — the UI forces the clinician to type a
   * short rationale so we can later audit the dismissal pattern.
   * Optional for the other actions.
   */
  note: z.string().trim().max(500).optional(),
});
export type RevenueAlertActionPayload = z.infer<typeof zRevenueAlertActionPayload>;

/**
 * Shape of the `ai_suggestions.payload` we write when mirroring an
 * alert into the Phase-3 cockpit. The renderer
 * (`src/components/review/RevenueAlertCard.tsx`) parses this with the
 * `isRevenueAlertSuggestionPayload` type guard so it never depends on
 * the raw DB row shape.
 */
export const zRevenueAlertSuggestionPayload = z.object({
  alert_id: z.string().uuid(),
  rule_id: z.string(),
  severity: zRevenueAlertSeverity,
  suggested_icd: zRevenueSuggestedIcd.nullable(),
  rationale: z.string(),
  citations: z.array(z.string()),
  estimated_roi_eur: z.number().nullable(),
  disclaimer: z.string(),
  suggested_actions: z.array(
    z.object({ kind: z.string(), label: z.string() }),
  ),
});
export type RevenueAlertSuggestionPayload = z.infer<typeof zRevenueAlertSuggestionPayload>;

/** Lightweight runtime type-guard for renderers. */
export function isRevenueAlertSuggestionPayload(
  p: unknown,
): p is RevenueAlertSuggestionPayload {
  return zRevenueAlertSuggestionPayload.safeParse(p).success;
}
